// packages/shared/src/lib/crypto/accessKey.ts
const PBKDF2_ITERATIONS = 250_000;

function b64(bytes: ArrayBuffer | Uint8Array): string {
  const arr = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let bin = ''; for (const b of arr) bin += String.fromCharCode(b);
  return btoa(bin);
}
function unb64(s: string): Uint8Array {
  const bin = atob(s); const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export interface EncryptedKeyFile {
  version: 1;
  keyId: string;
  alg: 'Ed25519';
  kdf: { name: 'PBKDF2'; hash: 'SHA-256'; iterations: number; salt: string };
  cipher: { name: 'AES-GCM'; iv: string };
  encryptedPrivateKey: string;
  publicKey: string;
}

export async function generateKeyPair() {
  const pair = await crypto.subtle.generateKey(
    { name: 'Ed25519' }, true, ['sign', 'verify'],
  ) as CryptoKeyPair;
  const spki = await crypto.subtle.exportKey('spki', pair.publicKey);
  return { publicKeyBase64: b64(spki), privateKey: pair.privateKey };
}

async function deriveAesKey(password: string, salt: Uint8Array): Promise<CryptoKey> {
  const baseKey = await crypto.subtle.importKey(
    'raw', new TextEncoder().encode(password), 'PBKDF2', false, ['deriveKey'],
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations: PBKDF2_ITERATIONS, hash: 'SHA-256' },
    baseKey, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt'],
  );
}

export async function sealPrivateKey(
  privateKey: CryptoKey, password: string,
  opts: { keyId: string; publicKeyBase64: string },
): Promise<EncryptedKeyFile> {
  const pkcs8 = await crypto.subtle.exportKey('pkcs8', privateKey);
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const aes = await deriveAesKey(password, salt);
  const enc = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, aes, pkcs8);
  return {
    version: 1, keyId: opts.keyId, alg: 'Ed25519',
    kdf: { name: 'PBKDF2', hash: 'SHA-256', iterations: PBKDF2_ITERATIONS, salt: b64(salt) },
    cipher: { name: 'AES-GCM', iv: b64(iv) },
    encryptedPrivateKey: b64(enc), publicKey: opts.publicKeyBase64,
  };
}

export async function unsealPrivateKey(file: EncryptedKeyFile, password: string): Promise<CryptoKey> {
  const aes = await deriveAesKey(password, unb64(file.kdf.salt));
  let pkcs8: ArrayBuffer;
  try {
    pkcs8 = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: unb64(file.cipher.iv) }, aes, unb64(file.encryptedPrivateKey),
    );
  } catch {
    throw new Error('bad-password');
  }
  // extractable: false — la clé privée ne doit jamais pouvoir être ré-exportée en clair
  // (défense en profondeur : keySession ne signe qu'avec, ne l'exporte jamais).
  return crypto.subtle.importKey('pkcs8', pkcs8, { name: 'Ed25519' }, false, ['sign']);
}

export async function signChallenge(privateKey: CryptoKey, challenge: string): Promise<string> {
  const sig = await crypto.subtle.sign('Ed25519', privateKey, new TextEncoder().encode(challenge));
  return b64(sig);
}

export async function importPublicKey(spkiBase64: string): Promise<CryptoKey> {
  return crypto.subtle.importKey('spki', unb64(spkiBase64), { name: 'Ed25519' } as AlgorithmIdentifier, true, ['verify']);
}

export async function verifyChallenge(publicKey: CryptoKey, challenge: string, signatureBase64: string): Promise<boolean> {
  return crypto.subtle.verify(
    'Ed25519', publicKey, unb64(signatureBase64), new TextEncoder().encode(challenge),
  );
}
